import { BehaviorSubject } from 'rxjs';
import { isNullish, nonNullish } from '../../shared/utils';
import {
  PipelineScenarioFilter,
  PipelineScenarioSearchCriteria,
  PipelineScenarioSort,
  ScenarioLayout,
  ScenarioRow,
  ViewIcon,
} from './scenario-pricing.models';
import { FeatureFlagRepository } from '../../core/state';
import { inject, signal } from '@angular/core';
import { isEqual } from 'lodash';
import { omitByPredicate } from '../utils/scenario-pricing.util';

export class ScenarioTableVM {
  readonly availablePageSizes = [10, 25, 50];
  defaultSort: PipelineScenarioSort = { columnName: 'lastPriced', isDescending: true };
  defaultSortOrder = -1;

  private _lastRefresh: Date;
  //TODO: make it private
  private _layout: ScenarioLayout;
  private _originalLayout: ScenarioLayout;
  private _featureFlagRepo = inject(FeatureFlagRepository);

  scenarioRows: ScenarioRow[];
  pageSize: number;
  pageIndex: number;
  totalRecords: number;
  globalFilterQuery: string;
  filters: PipelineScenarioFilter[] = [];
  sort: PipelineScenarioSort[] = [];
  isLayoutNeeded = true;
  pinned: Partial<Record<keyof ScenarioRow, boolean>> = {};
  columns: string[];
  columnWidth: Partial<Record<keyof ScenarioRow, number>> = {};
  originalColumnWidth: Partial<Record<keyof ScenarioRow, number>> = {};
  sortField$ = new BehaviorSubject<string | null>(null);
  sortOrder$ = new BehaviorSubject<number | null>(null);
  activeSort: string;
  layoutChanged = signal<boolean>(false);
  activeView: ViewIcon | string;
  /**
   * Selection is a list of Scenario Row selectd by the checkbox
   */
  selection: ScenarioRow[] = [];
  checkBoxColWidth = '95.625';

  constructor(pageSize: number = 50, activeView: ViewIcon | string) {
    this.scenarioRows = [];
    this.pageIndex = 0;
    this.pageSize = pageSize;
    this.globalFilterQuery = '';
    this.activeView = activeView;
  }

  get lastRefresh(): Date {
    return this._lastRefresh;
  }
  set lastRefresh(v: Date) {
    this._lastRefresh = v;
  }

  get pinnedCount() {
    return Object.values(this.pinned).reduce((acc, next) => (acc += next ? 1 : 0), 0);
  }

  reorderColumns(updatedPinnedColumn: string = null) {
    this.columns = [...this.columns].sort((a, b) => {
      if (this.pinned[a] === this.pinned[b]) {
        // place updated column right-most if pinned, left-most if unpinned
        if (a === updatedPinnedColumn) {
          return this.pinned[a] ? 1 : -1;
        }
        if (b === updatedPinnedColumn) {
          return this.pinned[a] ? -1 : 1;
        }
        return 0;
      }
      return this.pinned[a] ? -1 : 1;
    });

    // Update Display Orders for Column Layouts
    this.columns.forEach((field, index) => {
      const column = this.scenarioLayout.columns.find((c) => c.columnId === field);
      if (column) {
        column.displayOrder = index;
      }
    });
  }

  // TODO : Move Scenario Layout here.
  get scenarioLayout(): ScenarioLayout {
    const scenarioLayout = new ScenarioLayout();
    scenarioLayout.pageSize = this.pageSize;
    scenarioLayout.globalTextFilter = this.globalFilterQuery;
    scenarioLayout.displayOrder = this._layout?.displayOrder;
    scenarioLayout.guid = this._layout?.guid;
    scenarioLayout.name = this._layout?.name;
    scenarioLayout.userGUID = this._layout?.userGUID;
    scenarioLayout.columns = [...this.columnsData];

    return scenarioLayout;
  }
  set scenarioLayout(layout: ScenarioLayout) {
    this.pageSize = layout.pageSize;
    this.globalFilterQuery = layout.globalTextFilter;

    if (this.isLayoutNeeded) {
      this._layout = JSON.parse(JSON.stringify(layout));
      this._originalLayout = JSON.parse(JSON.stringify(layout));
    }

    (this._layout?.columns ?? []).forEach((column) => {
      this.columnWidth[column.columnId] = column.columnWidth ? `${column.columnWidth}px` : '';
    });

    this.columns = this.columnsDataSorted.map((c) => c.columnId);

    if (!this.sortedColumn) {
      const lastPricedCol = this.getColumnById('lastPriced');
      lastPricedCol.sortPriority = 0;
      lastPricedCol.sortDirection = 'Descending';
    }

    const sortedColumn = this.sortedColumn;
    if (this.sortedColumn) {
      if (sortedColumn.sortDirection === 'Descending') {
        this.sort = [
          {
            columnName: sortedColumn.columnId,
            isDescending: true,
          },
        ];
      } else {
        this.sort = [
          {
            columnName: sortedColumn.columnId,
          },
        ];
      }
    }

    this.columnsDataSorted.forEach((column) => {
      if (column.isPinned) {
        this.pinned[column.columnId] = true;
      }
      if (nonNullish(column.sortPriority) && !column.isGroupedBy) {
        this.sortField$.next(column.columnId);
        this.sortOrder$.next(column.sortDirection === 'Descending' ? -1 : 1);
        this.activeSort = this.sortField$.value;
      }
    });
    this.originalColumnWidth = JSON.parse(JSON.stringify(this.columnWidth));
  }

  undoLayoutChanges() {
    this.sortField$.next(null);
    this.sortOrder$.next(null);
    this.activeSort = this.sortField$.value;
    this.pinned = {};
    this.globalFilterQuery = this._originalLayout.globalTextFilter;
    this.pageSize = this._originalLayout.pageSize;
    this.sort = [...this.originalSort];
    this.isLayoutNeeded = true;
  }

  onSaveLayout() {
    this._originalLayout = JSON.parse(JSON.stringify(this.scenarioLayout));
    this.originalColumnWidth = JSON.parse(JSON.stringify(this.columnWidth));
  }

  onLayoutChange() {
    this.layoutChanged.set(this._layoutChanged() ? true : false);
  }

  private _layoutChanged() {
    if (this._originalLayout.viewType === 'List') {
      this.scenarioLayout.columns.forEach((column) => {
        for (const key in column) {
          if (column[key] === undefined) {
            delete column[key];
          }
        }
      });

      const predicate = (value) => isNullish(value);
      const columnsChanged = !isEqual(
        omitByPredicate(this._originalLayout.columns, predicate),
        omitByPredicate(this.scenarioLayout.columns, predicate),
      );

      return (
        columnsChanged ||
        this._originalLayout.pageSize !== this.scenarioLayout.pageSize ||
        (this._originalLayout.globalTextFilter ?? null) !== (this.scenarioLayout.globalTextFilter ?? null) ||
        this._originalLayout.displayOrder !== this.scenarioLayout.displayOrder ||
        this._originalLayout.name !== this.scenarioLayout.name
      );
    }

    // The card view is responsible only for its own page size and scenario layout name.
    return this._originalLayout.pageSize !== this.scenarioLayout.pageSize || this._originalLayout.name !== this.scenarioLayout.name;
  }

  getColumnById(columnId: string) {
    return this.columnsData.find((column) => column.columnId === columnId);
  }

  get sortedColumn() {
    return (this._layout?.columns ?? []).find((column) => column.sortPriority === 0);
  }

  get columnsData() {
    return this._layout?.columns ?? [];
  }

  get columnsDefault() {
    return this.columnsDataSorted.map((c) => c.columnId);
  }

  get columnsDataSorted() {
    return (this._layout?.columns ?? [])
      .filter((c) => c.displayOrder !== null && c.displayOrder > -1)
      .sort((a, b) => a.displayOrder - b.displayOrder);
  }

  get originalColumns() {
    return this._originalLayout.columns
      .filter((c) => c.displayOrder !== null && c.displayOrder > -1)
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map((c) => c.columnId);
  }

  get columnWidthsState() {
    const columnWidths: string[] = [this.checkBoxColWidth];
    this.originalColumns.forEach((column, index) => {
      columnWidths[index + 1] = this._originalLayout.columns?.find((col) => col.columnId === column).columnWidth?.toString();
    });
    return columnWidths.join(',');
  }

  get criteria() {
    const criteria: PipelineScenarioSearchCriteria = {
      filters: [],
      sort: [],
    };
    criteria.filters = this.filters;
    criteria.globalSearchQuery = this.globalFilterQuery;
    criteria.isLayoutNeeded = this.isLayoutNeeded;
    criteria.sort = this.sort;
    criteria.topN = this.pageSize;
    criteria.offsetN = this.pageIndex * this.pageSize;
    criteria.activeView = this.activeView;
    return criteria;
  }

  get defaultSortField() {
    return this.defaultSort.columnName as keyof ScenarioRow;
  }

  get originalSort(): PipelineScenarioSort[] {
    // TODO: Add GroupBy to this logic when it's ready
    const column = this._originalLayout.columns.find((column) => column.sortPriority === 0 && !column.isGroupedBy);
    if (column.sortDirection === 'Descending') {
      return [
        {
          columnName: column.columnId,
          isDescending: true,
        },
      ];
    } else {
      return [
        {
          columnName: column.columnId,
        },
      ];
    }
  }

  // applyFilter
  // pin/Unpin
}
